// Страница поиска /search?q= (T137; ux.md, раздел 8: «Все результаты по «…»» палитры ведёт сюда;
// contracts/ui.md, «/search»). Поле поиска — на каждое нажатие с короткой задержкой, ввод —
// в адресе. Результаты — по разделам, доступным ролям (features/search/sources.ts): «Разделы»
// (названия разделов меню), «Пользователи», «Доступ сопровождения», «Журнал
// действий» — до 20 строк в разделе и ссылка «Все» на раздел с тем же поиском. Раздел без
// совпадений не показывается; сбой раздела — выноска с причиной, кодом, номером операции
// и «Повторить», а не пустой результат (T135); на время повтора раздел с выноской на месте,
// на «Повторить» — спиннер (T168). Пока первые ответы не пришли — один скелет
// в форме списка (экран не прыгает: README «Состояния»); при новом вводе видны прежние
// результаты. Пусто — «Что найти?» (меньше двух букв) или «Ничего не найдено» с «Очистить
// поиск» (ux.md, раздел 6).
//
// 047 (T012; DNS-16, DNS-17, DNS-19): видно, почему строка найдена — слово выделено в названии
// и подписи строк всех разделов (Highlight, правило поиска сервера); у пункта, где слова нет
// в названии, под ним — «<Поле>: …отрывок…» одной строкой. Поле поиска на странице одно:
// «Найти или перейти…» шапки здесь — значок (shell/Topbar.tsx).
import { Button, EmptyState, PageHeader, SearchField, Skeleton, SkeletonRow } from '@kp/ui';
import type { LinkProps } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import { Search } from '@kp/ui/icons';
import { useEffect, useId, useState } from 'react';
import { matchesTitle } from '../../shell/CommandPalette.tsx';
import { sectionLinks } from '../../shell/settings-sections.ts';
import { useMe } from '../../shell/session.ts';
import { ListLoadError } from '../load-error.tsx';
import { Highlight } from './highlight.ts';
import {
  SEARCH_FROM,
  allowedSources,
  searchHint,
  searchPlaceholder,
  useSearchResults,
} from './sources.ts';
import type { SourceResult } from './sources.ts';
import './search.css';

const SEARCH_DELAY_MS = 250;
/** Строк в разделе страницы поиска; дальше — «Все» в самом разделе. */
export const SEARCH_PAGE_LIMIT = 20;
/** Длина поиска — как у списков сервера. */
const SEARCH_MAX = 200;

export interface SearchPageSearch {
  q?: string;
}

/** Для адреса страницы: validateSearch маршрута /search. */
export function parseSearchPageSearch(search: Record<string, unknown>): SearchPageSearch {
  const q = typeof search.q === 'string' ? search.q : undefined;
  return q && q.trim() !== '' && q.trim().length <= SEARCH_MAX ? { q } : {};
}

/** «Ищет: пользователи, доступ сопровождения, журнал действий» — по правам ролей. */
export function searchScopeNote(headings: readonly string[]): string {
  return `Ищет по ФИО, почте и названиям: ${headings
    .map((heading) => heading.toLocaleLowerCase('ru-RU'))
    .join(', ')}`;
}

export function SearchPage({
  search,
  onSearchChange,
}: {
  search: SearchPageSearch;
  onSearchChange: (next: SearchPageSearch) => void;
}) {
  const me = useMe();
  const sources = allowedSources(me.permissions);

  // Поиск идёт на каждое нажатие с короткой задержкой; адрес страницы — источник ввода.
  const [text, setText] = useState(search.q ?? '');
  const [syncedQ, setSyncedQ] = useState(search.q);
  if (search.q !== syncedQ) {
    setSyncedQ(search.q);
    setText(search.q ?? '');
  }
  useEffect(() => {
    const q = text.trim() === '' ? undefined : text;
    if (q === search.q) return undefined;
    const timer = window.setTimeout(() => onSearchChange(q ? { q } : {}), SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [text, search, onSearchChange]);

  const q = (search.q ?? '').trim();
  const ready = q.length >= SEARCH_FROM;
  const results = useSearchResults(q, { limit: SEARCH_PAGE_LIMIT, enabled: true });
  const sectionHits = ready
    ? sectionLinks(me.permissions).filter((item) => matchesTitle(item.label, q))
    : [];
  const shown = results.filter((result) => result.hits.length > 0 || result.failure);
  const settled = results.every((result) => !result.pending);
  const empty = sectionHits.length === 0 && shown.length === 0;
  const nothing = ready && settled && empty;

  const clear = () => {
    setText('');
    onSearchChange({});
  };

  let body;
  if (!ready) {
    body = <EmptyState icon={Search} title="Что найти?" text={searchHint(me.permissions)} />;
  } else if (empty && !settled) {
    body = (
      <Skeleton minHeight={120} className="kp-search-skeleton">
        <SkeletonRow width="40%" />
        <SkeletonRow width="65%" />
        <SkeletonRow width="55%" />
      </Skeleton>
    );
  } else if (nothing) {
    body = (
      <EmptyState
        title="Ничего не найдено"
        text={`Нет совпадений по «${q}». Проверьте раскладку или откройте раздел из меню`}
        action={<Button onClick={clear}>Очистить поиск</Button>}
      />
    );
  } else {
    body = (
      <>
        {sectionHits.length > 0 ? (
          <ResultSection heading="Разделы" count={sectionHits.length}>
            <ul className="kp-list">
              {sectionHits.map((item) => (
                <li key={item.to} className="kp-list-row">
                  <Link to={item.to as LinkProps['to']} className="kp-link">
                    <Highlight text={item.label} q={q} />
                  </Link>
                  {item.sub ? (
                    <span className="t-muted">
                      <Highlight text={item.sub} q={q} />
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </ResultSection>
        ) : null}
        {shown.map((result) => (
          <SourceSection key={result.id} result={result} q={q} />
        ))}
      </>
    );
  }

  return (
    <main id="main" tabIndex={-1} className="kp-page kp-search-page">
      <PageHeader title="Поиск" note={searchScopeNote(sources.map((source) => source.heading))} />
      <SearchField
        label="Что найти"
        placeholder={searchPlaceholder(me.permissions)}
        className="kp-search-page-field"
        value={text}
        maxLength={SEARCH_MAX}
        onChange={(event) => setText(event.target.value)}
      />
      {body}
    </main>
  );
}

function ResultSection({
  heading,
  count,
  action,
  children,
}: {
  heading: string;
  count?: number;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  const id = useId();
  return (
    <section className="kp-section kp-search-section" aria-labelledby={id}>
      <h2 className="t-sec-h" id={id}>
        {heading}
        {count !== undefined ? <span className="t-cnt t-num">{count}</span> : null}
        {action ? <span className="kp-sec-action">{action}</span> : null}
      </h2>
      {children}
    </section>
  );
}

function SourceSection({ result, q }: { result: SourceResult; q: string }) {
  let content;
  if (result.failure) {
    content = (
      <ListLoadError
        what={`Не удалось найти ${result.what}`}
        view={result.failure}
        retrying={result.retrying}
        onRetry={result.retry}
      />
    );
  } else {
    content = (
      <ul className="kp-list">
        {result.hits.map((hit) => (
          <li key={hit.id} className="kp-list-row kp-list-2">
            <Link {...(hit.link as LinkProps)} className="kp-link">
              <Highlight text={hit.label} q={q} />
            </Link>
            {hit.snippet ? (
              <span className="t-muted kp-search-snippet">
                {hit.snippet.label}: <Highlight text={hit.snippet.text} q={q} />
              </span>
            ) : null}
            {hit.sub ? (
              <span className="t-muted t-num">
                <Highlight text={hit.sub} q={q} />
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    );
  }
  return (
    <ResultSection
      heading={result.heading}
      count={result.failure ? undefined : result.hits.length}
      action={
        result.hits.length > 0 ? (
          <Link
            {...(result.section as LinkProps)}
            className="kp-link"
            aria-label={`Все результаты в разделе «${result.heading}»`}
          >
            Все
          </Link>
        ) : undefined
      }
    >
      {content}
    </ResultSection>
  );
}
